import { useEffect, useRef, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import type { Customer } from '../components/CustomerManagement';
import { KundendatenPanel, type VollmachtAngaben, type AntragPage as AntragSeite } from '../components/KundendatenPanel';
import type { BevollmaechtigteProfil } from '../lib/bevollmaechtigte';
import { api } from '../lib/api';
import { buildDiagnosticsText, fillFields, detectSubmissionConfirmation, type FillField } from '../lib/bafaFormFill';

const BAFA_DIRECT = 'https://fms.bafa.de/BafaFrame/v2/ubf3';
const BAFA_PROXY = '/api/bafa-proxy/BafaFrame/v2/ubf3';

interface ProfileResponse extends BevollmaechtigteProfil {
  contactPerson?: string;
  advisorNumber?: string;
  teamOwnerAdvisorNumber?: string | null;
  teamOwnerContactPerson?: string | null;
  teamOwnerPhone?: string | null;
  teamOwnerEmail?: string | null;
  teamOwnerBeraterAnrede?: string | null;
}

interface Consultant extends BevollmaechtigteProfil {
  contactPerson?: string;
  advisorNumber?: string;
}

export default function AntragPage() {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [consultant, setConsultant] = useState<Consultant | null>(null);
  const [vollmacht, setVollmacht] = useState<VollmachtAngaben | null>(null);
  const [debugText, setDebugText] = useState<string | null>(null);
  // Welche BAFA-Antragsseite der iframe gerade zeigt — steuert, welcher Abschnitt im Panel
  // rechts sichtbar ist (Kundenwunsch: vorher stand immer der komplette Datensatz da).
  // 'ozg' optimistisch als Start, der Proxy-Link öffnet immer dort; onLoad korrigiert bei
  // jeder weiteren Navigation (BAFA ist serverseitig gerendert → echter Seitenwechsel je
  // Schritt, kein SPA-Routing ohne onLoad-Trigger).
  const [antragPage, setAntragPage] = useState<AntragSeite>('ozg');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const antragGemeldetRef = useRef(false);

  const handleIframeLoad = () => {
    try {
      const pathname = iframeRef.current?.contentWindow?.location?.pathname || '';
      const doc = iframeRef.current?.contentDocument || null;
      if (pathname.endsWith('/ubf3/ozg')) { setAntragPage('ozg'); return; }
      if (pathname.endsWith('/ubf3/personalInfo/bevollmaechtigter')) { setAntragPage('bevollmaechtigter'); return; }
      if (pathname.endsWith('/ubf3/personalInfo/antragsteller')) {
        // Dieselbe URL für zwei Varianten — „für mich selbst" fragt die E-Mail zur
        // Bestätigung ein zweites Mal ab (Feld „email2"), die Variante nach Bevollmächtigung
        // nicht. Das unterscheidet sie, nicht die URL.
        setAntragPage(doc?.getElementById('email2') ? 'antragsteller-selbst' : 'antragsteller-unternehmen');
        return;
      }
      if (pathname.endsWith('/ubf3/angaben_zum_beratungsunternehmen')) { setAntragPage('beratungsunternehmen'); return; }
      if (pathname.endsWith('/ubf3/unternehmensdaten')) { setAntragPage('unternehmensdaten'); return; }
      if (pathname.endsWith('/ubf3/bestaetigungen')) { setAntragPage('bestaetigungen'); return; }
      // Standort, Leitstelle, sowie alles nach „Bestätigungen" (Dokument-Upload/Abschluss):
      // kein Datenvorschlag, siehe KundendatenPanel.
      setAntragPage('andere');
    } catch {
      // Cross-Origin (z.B. während des BAFA-Logins, noch nicht im Proxy) — Zustand unverändert.
    }
  };

  /** Temporäre Diagnose-Hilfe: zeigt, welche Formularfelder im iframe tatsächlich
   *  ankommen — Label-Matching lief beim ersten Test komplett ins Leere. */
  const runDiagnostics = () => {
    let doc: Document | null = null;
    try {
      doc = iframeRef.current?.contentDocument || null;
    } catch (e) {
      setDebugText(`Kein Zugriff auf iframe-Inhalt (Cross-Origin): ${String(e)}`);
      return;
    }
    if (!doc) {
      setDebugText('Kein Zugriff auf iframe-Inhalt (contentDocument ist null — vermutlich andere Domain / Cross-Origin, z.B. während des Logins).');
      return;
    }
    setDebugText(buildDiagnosticsText(doc, iframeRef.current?.src));
  };

  const handleFillSection = async (fields: FillField[]) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const missed = await fillFields(doc, fields);
    if (missed.length) {
      alert(`Nicht gefunden im Formular: ${missed.join(', ')}\n\nBitte manuell eintragen — restliche Felder wurden ausgefüllt.`);
    }
  };

  useEffect(() => {
    const raw = sessionStorage.getItem('antragCustomer');
    if (raw) {
      try {
        const geladen: Customer = JSON.parse(raw);
        setCustomer(geladen);
        // Bevollmächtigten-Angaben aus der Vollmacht dieses Kunden — schlagen das
        // Berater-Profil, damit im Antrag dasselbe steht wie im unterschriebenen
        // Dokument. Fehlt/scheitert das, bleibt es still beim Profil.
        if (geladen?.id) {
          api.get<VollmachtAngaben | null>(`/api/customers/${geladen.id}/vollmacht-bevollmaechtigte`)
            .then(setVollmacht)
            .catch(() => {});
        }
      } catch { /* ignore */ }
    }
    api.get<ProfileResponse>('/api/auth/me').then((profile) => {
      setConsultant({
        // Profil vollständig durchreichen — der Bevollmächtigungs-Block braucht
        // Firmenanschrift, Team-Zugehörigkeit, Kontaktdaten des Inhabers und ggf.
        // die abweichende Person. Wer als Bevollmächtigter auftritt, entscheidet
        // `resolveBevollmaechtigte` — eine Regel, gleichlautend mit der Vollmacht.
        ...profile,
        // Der Block „Angaben zum Beratungsunternehmen" hängt dagegen an der BAFA-ID:
        // ohne eigene wird unter der des Team-Inhabers beantragt.
        advisorNumber: profile.advisorNumber || profile.teamOwnerAdvisorNumber || undefined,
        contactPerson: profile.advisorNumber
          ? profile.contactPerson
          : (profile.teamOwnerContactPerson || profile.contactPerson),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!customer?.id) return;
    const customerId = customer.id;
    const timer = window.setInterval(() => {
      if (antragGemeldetRef.current) return;
      let doc: Document | null = null;
      try { doc = iframeRef.current?.contentDocument || null; } catch { return; }
      if (!doc || !detectSubmissionConfirmation(doc)) return;
      antragGemeldetRef.current = true;
      api.post('/api/customer-events/antrag-eingereicht', { customerId }).catch(() => {});
    }, 4000);
    return () => window.clearInterval(timer);
  }, [customer?.id]);

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
      {/* Schmale Headerleiste */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#fff' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          {customer ? `Antrag — ${customer.name}` : 'BAFA Antragsformular'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={runDiagnostics}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Diagnose
          </button>
          <a
            href={BAFA_DIRECT}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#7c3aed', textDecoration: 'none' }}
          >
            <ExternalLink size={13} />
            Direkt öffnen
          </a>
          <button
            onClick={() => window.close()}
            title="Tab schließen"
            style={{ display: 'flex', alignItems: 'center', color: '#64748b', background: 'none', border: 'none', padding: 4, cursor: 'pointer', borderRadius: 6 }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Inhalt: iframe + Panel */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <iframe
          ref={iframeRef}
          src={BAFA_PROXY}
          style={{ flex: 1, border: 'none' }}
          title="BAFA Antragsformular"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation allow-popups-to-escape-sandbox"
          onLoad={handleIframeLoad}
        />
        {customer && (
          <div style={{ width: 400, flexShrink: 0, borderLeft: '1px solid #e2e8f0', overflowY: 'auto', background: '#f8fafc' }}>
            <KundendatenPanel customer={customer} consultant={consultant} vollmacht={vollmacht} onFillSection={handleFillSection} page={antragPage} />
          </div>
        )}
      </div>

      {debugText && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, width: '90%', maxWidth: 700, maxHeight: '80%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Formular-Diagnose (bitte Text kopieren und schicken)</strong>
              <button onClick={() => setDebugText(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={16} />
              </button>
            </div>
            <textarea
              readOnly
              value={debugText}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              style={{ flex: 1, fontFamily: 'monospace', fontSize: 11, resize: 'none', minHeight: 300 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
